import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import { BaseComponent, FeatureType, Grid, JobHazardAnalysis, ListItem, ModalSize, SafetyFeedback, User, VpJob } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { SafetyFeedbackComponent } from './safety-feedback.component'

@Component({
    selector: 'app-safety-feedbacks',
    templateUrl: './safety-feedbacks.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class SafetyFeedbacksComponent extends BaseComponent {
    faSearch = faSearch
    public grid: Grid<SafetyFeedback>

    // Grid display maps (all jobs / all users so inactive/closed still render).
    jobMap!: DataMap
    employeeMap!: DataMap

    // Modal pick lists (active only).
    activeJobList: ListItem<string>[] = []
    activeUserList: ListItem<string>[] = []
    jhaList: ListItem<string>[] = []

    get currentUserId(): string {
        return this.services.authentication.userId()
    }

    constructor() {
        super()
        this.grid = new Grid<SafetyFeedback>(this.services)
            .setupModalEditing(this.services.data.safetyFeedbacks, 'Safety Feedback', SafetyFeedbackComponent, () => new SafetyFeedback(), this, ModalSize.large)
            .wrapColumnHeaders()
            .filterBy(item => [item.job, item.eventDescription, item.reportedTo])
        this.grid.canAdd = this.canAddFeedback
        this.grid.canEdit = this.canEditFeedback
        this.grid.canDelete = this.canEditFeedback
        this.refresh()
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAllByAction('GetAllJobs'),
                    this.services.data.users.queryAllByAction('GetForLookup'),
                    this.services.data.safetyFeedbacks.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        const allJobs = result[0] as VpJob[]
                        const allUsers = result[1] as User[]

                        const jobListItems = allJobs.map(j => new ListItem(j.id, j.id.trim() + ' ' + j.name))
                        this.jobMap = new DataMap(jobListItems, 'value', 'display')
                        this.employeeMap = new DataMap(allUsers, 'id', 'displayName')

                        this.activeJobList = allJobs
                            .filter(j => j.jobStatus === 1)
                            .sort((a, b) => a.id.trim().localeCompare(b.id.trim(), undefined, { numeric: true }))
                            .map(j => new ListItem(j.id, j.id.trim() + ' ' + j.name))
                        this.activeUserList = allUsers.filter(u => u.active).map(u => new ListItem(u.id, u.displayName))

                        this.grid.items = result[2] as SafetyFeedback[]

                        if (this.canEditFeedback) {
                            this.loadJhaList()
                        }
                    },
                }),
        )
    }

    // Loaded separately (and tolerant of failure) so a missing JHA-view permission doesn't break the page.
    private loadJhaList() {
        this.services.data.jobHazardAnalyses.queryAll().subscribe({
            next: (jhas: JobHazardAnalysis[]) => {
                this.jhaList = jhas
                    .filter(j => !j.archived)
                    .sort((a, b) => (b.indexId ?? 0) - (a.indexId ?? 0))
                    .map(j => new ListItem(j.id, this.jhaDisplay(j)))
            },
            error: () => {
                this.jhaList = []
            },
        })
    }

    private jhaDisplay(jha: JobHazardAnalysis): string {
        const job = (jha.job ?? '').trim()
        const date = jha.date ? new Date(jha.date).toLocaleDateString() : ''
        const parts = [`JHA #${jha.indexId}`]
        if (job) parts.push(job)
        if (date) parts.push(date)
        return parts.join(' - ')
    }

    // View grants add; Edit grants edit/delete (Edit is a superset of View).
    public get canAddFeedback() {
        return this.services.authentication.hasFeature([FeatureType.SafetyFeedbackView, FeatureType.SafetyFeedbackEdit])
    }

    public get canEditFeedback() {
        return this.services.authentication.hasFeature([FeatureType.SafetyFeedbackEdit])
    }
}
